import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { createLogger } from '@/utils/logger';

const log = createLogger('PdfCache');
type ProtectedKeyResolver = () => Promise<string | null>;
type CachedPdfRecord = PdfCacheDB['pdfs']['value'];

let protectedKeyResolver: ProtectedKeyResolver | null = null;

export function setPdfCacheProtectedKeyResolver(resolver: ProtectedKeyResolver): void {
  protectedKeyResolver = resolver;
}

/**
 * Cache key for a cloud document's PDF. `revision` (the review session's
 * version / packageHash) versions re-ingested files that retain their path.
 * The legacy URL argument was removed because download URLs are no longer
 * stored or used as Storage read capabilities.
 */
export function cloudPdfCacheKey(storagePath: string, revision?: string | null): string {
  return revision ? `${storagePath}@${revision}` : storagePath;
}

/**
 * The content revision of a cloud document, from the fields a review
 * session's `markups.ingest` bumps (`version`, `packageHash`); undefined for an
 * ordinary uploaded document, whose cache key then stays exactly as before.
 */
export function documentRevision(document: { version?: number; packageHash?: string }): string | undefined {
  if (document.version === undefined && !document.packageHash) return undefined;
  return `${document.version ?? ''}:${document.packageHash ?? ''}`;
}

interface PdfCacheDB extends DBSchema {
  pdfs: {
    key: string;
    value: {
      blob: Blob;
      storagePath: string;
      cachedAt: number;
      size: number;
    };
  };
}

/**
 * IndexedDB-based PDF cache service.
 *
 * Reduces storage bandwidth costs by:
 * - Caching PDFs in browser for 7 days
 * - Avoiding repeated downloads from Firebase Storage
 * - Auto-evicting old entries when cache exceeds 100MB
 *
 * Expected savings: 50% storage egress for repeat visits
 */
class PdfCacheService {
  private db: IDBPDatabase<PdfCacheDB> | null = null;
  private readonly MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days
  private readonly MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100MB

  async init() {
    if (this.db) return; // Already initialized

    this.db = await openDB<PdfCacheDB>('pdf-cache', 1, {
      upgrade(db) {
        db.createObjectStore('pdfs');
      },
    });
  }

  /**
   * Get cached PDF blob if available and not expired.
   *
   * Returns null when the entry is absent or expired (a cache miss).
   * Throws on an actual IDB failure so callers can distinguish "gone" from "broken".
   * The cloud caller (useViewerContext) wraps this in its own try-catch and treats
   * errors as optional-cache misses; the local caller lets errors propagate.
   */
  async getCachedPdf(storagePath: string): Promise<Blob | null> {
    if (!this.db) await this.init();

    // Let IDB errors propagate -- callers distinguish miss (null) from error (thrown).
    const cached = await this.db!.get('pdfs', storagePath);
    if (!cached) return null;

    // The live local workspace's blob (the protected key) expires with its workspace, not by its own age:
    // LocalWorkspaceService.isExpired decides, and a workspace with photos never expires (image spec 4.2).
    if (Date.now() - cached.cachedAt > this.MAX_AGE && storagePath !== (await this.getProtectedKey())) {
      await this.db!.delete('pdfs', storagePath);
      return null;
    }

    log.debug(`Cache HIT for ${storagePath}`);
    return cached.blob;
  }

  /**
   * Refresh the cachedAt timestamp for an existing entry, extending its TTL.
   * Used by LocalWorkspaceService.touchWorkspace() to keep the PDF blob and
   * the workspace on the same expiry clock. Silently no-ops if the entry is gone.
   */
  async refreshCachedAt(storagePath: string): Promise<void> {
    if (!this.db) await this.init();

    try {
      const cached = await this.db!.get('pdfs', storagePath);
      if (!cached) return; // entry is gone -- idempotent
      await this.db!.put('pdfs', { ...cached, cachedAt: Date.now() }, storagePath);
      log.debug(`Refreshed cachedAt for ${storagePath}`);
    } catch (error) {
      log.warn('Could not refresh PDF cache timestamp', error);
    }
  }

  /**
   * Cache a PDF blob for the cloud viewer path where caching is optional.
   * Errors are swallowed so a quota-exceeded write does not break the cloud viewer.
   * Use cachePdfRequired() for the local viewer path where the blob is primary storage.
   */
  async cachePdf(storagePath: string, blob: Blob): Promise<void> {
    if (!this.db) await this.init();

    try {
      await this.evictIfNeeded(blob.size, [await this.getProtectedKey()]);
      await this.db!.put('pdfs', {
        blob,
        storagePath,
        cachedAt: Date.now(),
        size: blob.size,
      }, storagePath);
      log.debug(`Cached ${(blob.size / 1024 / 1024).toFixed(2)}MB for ${storagePath}`);
    } catch (error) {
      log.error('Error caching PDF', error);
      // Don't throw -- caching is optional for the cloud path
    }
  }

  /**
   * Cache a PDF blob for the local viewer path where the blob is primary storage.
   * Unlike cachePdf(), this method throws on failure so openLocalPdf() can surface
   * the error and abort workspace creation rather than recording a dangling blob key.
   */
  async cachePdfRequired(storagePath: string, blob: Blob): Promise<void> {
    if (!this.db) await this.init();

    // Protect both the incoming blob and any existing workspace blob so that
    // writing a new local PDF cannot evict a workspace the user still has open.
    const existingProtectedKey = await this.getProtectedKey();
    await this.evictIfNeeded(blob.size, [storagePath, existingProtectedKey]);
    await this.db!.put('pdfs', {
      blob,
      storagePath,
      cachedAt: Date.now(),
      size: blob.size,
    }, storagePath);
    log.debug(`Cached (required) ${(blob.size / 1024 / 1024).toFixed(2)}MB for ${storagePath}`);
  }

  /**
   * Evict old entries if cache would exceed size limit.
   * protectedKeys: keys that must never be evicted (e.g. the incoming new blob
   * and the existing workspace blob that is still in use).
   */
  private async evictIfNeeded(newSize: number, protectedKeys: (string | null)[] = []): Promise<void> {
    const protected_ = new Set(protectedKeys.filter((k): k is string => k !== null));
    const now = Date.now();
    const entries = await this.getStoredEntries();
    let totalSize = entries.reduce((sum, { entry }) => sum + entry.size, 0);

    const expiredEntries = entries.filter(({ key, entry }) =>
      !protected_.has(key) && now - entry.cachedAt > this.MAX_AGE
    );

    for (const { key, entry } of expiredEntries) {
      await this.db!.delete('pdfs', key);
      totalSize -= entry.size;
      log.debug(`Purged expired ${key}`);
    }

    if (totalSize + newSize > this.MAX_TOTAL_SIZE) {
      log.debug(`Evicting old entries (current: ${(totalSize / 1024 / 1024).toFixed(2)}MB)`);

      const expiredKeys = new Set(expiredEntries.map(({ key }) => key));
      const liveEntries = entries
        .filter(({ key }) => !protected_.has(key) && !expiredKeys.has(key))
        .sort((a, b) => a.entry.cachedAt - b.entry.cachedAt);

      for (const { key, entry } of liveEntries) {
        if (totalSize + newSize <= this.MAX_TOTAL_SIZE) break;
        await this.db!.delete('pdfs', key);
        totalSize -= entry.size;
        log.debug(`Evicted ${key}`);
      }
    }

    if (totalSize + newSize > this.MAX_TOTAL_SIZE && protected_.size > 0) {
      log.warn(
        `Cache size budget exceeded while preserving protected PDF blob(s): ${[...protected_].join(', ')}`
      );
    }
  }

  /**
   * Clear all cached PDFs
   */
  async clear(): Promise<void> {
    if (!this.db) await this.init();
    await this.db!.clear('pdfs');
    log.debug('Cache cleared');
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<{ count: number; totalSize: number }> {
    if (!this.db) await this.init();

    const entries = await this.getStoredEntries();
    const totalSize = entries.reduce((sum, { entry }) => sum + entry.size, 0);

    return {
      count: entries.length,
      totalSize,
    };
  }

  private async getProtectedKey(): Promise<string | null> {
    if (!protectedKeyResolver) return null;

    try {
      return await protectedKeyResolver();
    } catch (error) {
      log.warn('Could not resolve protected PDF cache key', error);
      return null;
    }
  }

  private async getStoredEntries(): Promise<Array<{ key: string; entry: CachedPdfRecord }>> {
    const allKeys = await this.db!.getAllKeys('pdfs');
    const entries = await Promise.all(
      allKeys.map(async (key) => ({ key, entry: await this.db!.get('pdfs', key) }))
    );

    return entries.filter(
      (item): item is { key: string; entry: CachedPdfRecord } => item.entry !== null
    );
  }
}

// Export singleton instance
export const pdfCache = new PdfCacheService();

